/* eslint-disable @typescript-eslint/ban-ts-comment */
import { Formik } from 'formik'
import { QRCodeSVG } from 'qrcode.react'
import * as React from 'react'
import { toast } from 'react-toastify'
import * as Yup from 'yup'

import { useAuthContext } from '@/auth/authContext'
import FormikPasswordField from '@/components/formik/FormikPasswordField'
import FormikTextField from '@/components/formik/FormikTextField'
import Spinner from '@/components/Spinner'
import {
  useDisableMFA,
  useSetupMFA,
  useVerifyMFA,
} from '@/queries/users/mfa/useMutateMFA'
import { useLoadUserDetails } from '@/queries/users/useLoadUsers'
import Button from '@/ui/atoms/Button'
import CopyButton from '@/ui/atoms/CopyButton'
import Flex from '@/ui/atoms/Flex'
import Modal from '@/ui/atoms/Modal'
import Space from '@/ui/atoms/Space'
import Text from '@/ui/atoms/Text'
import { ZentakeContext } from '@/utils/ZentakeContext'

const issuer = 'Zentake'

export default function ProfileTabMFA() {
  const [otpUri, setOTPUri] = React.useState<string | undefined>(undefined)
  const [isOpen, setIsOpen] = React.useState(false)
  const { disableMFA, isLoading: isDisableMFALoading } = useDisableMFA()
  const { setupMFA, isLoading: isSetupMFALoading } = useSetupMFA()
  const { verifyMFA, isLoading: isVerifyMFALoading } = useVerifyMFA()
  const { data: userDetails, isLoading: isUserDetailsLoading } =
    useLoadUserDetails({})
  const { setAlertConfig } = React.useContext(ZentakeContext)
  const { getProviderUser } = useAuthContext()
  const mfaEnabled = !!userDetails?.mfa_enabled
  const mfaSetupComplete = !!userDetails?.mfa_setup_complete
  const isLoading =
    isUserDetailsLoading ||
    isDisableMFALoading ||
    isSetupMFALoading ||
    isVerifyMFALoading

  const prefix = React.useMemo(() => {
    if (mfaSetupComplete) return 'Disable'
    if (mfaEnabled) return 'Finish setting up'
    return 'Setup'
  }, [mfaEnabled, mfaSetupComplete])

  const buildOTPUri = React.useCallback(
    async (secret: string): Promise<string | undefined> => {
      const user = await getProviderUser()
      if (!user) return
      const otpAuthUri =
        'otpauth://totp/' +
        // @ts-ignore
        `${encodeURIComponent(issuer)}:${encodeURIComponent(user?.signInDetails?.loginId)}` +
        `?secret=${encodeURIComponent(secret)}&issuer=${encodeURIComponent(issuer)}&algorithm=SHA1&digits=6&period=30`
      setOTPUri(otpAuthUri)
    },
    [getProviderUser]
  )

  const handleSetupMFA = React.useCallback(
    async (password: string) => {
      const response = await setupMFA(password)
      await buildOTPUri(response.secret_code)
    },
    [buildOTPUri, setupMFA]
  )

  const handleVerifyMFA = React.useCallback(
    async (code: string) => {
      const response = await verifyMFA(code)
      if (response.success) {
        toast.success('MFA enabled successfully')
        setIsOpen(false)
      }
    },
    [verifyMFA]
  )

  const showDeleteConfirmation = React.useCallback(
    async () =>
      new Promise((resolve) =>
        setAlertConfig({
          title: `Are you sure you want to disable MFA?`,
          description: 'Note: This will remove your MFA from your account.',
          buttons: [
            {
              title: 'Disable',
              style: 'primary',
              danger: true,
              onPress: () => resolve(true),
            },
            {
              title: 'Cancel',
              onPress: () => resolve(false),
            },
          ],
          canDismiss: false,
        })
      ),
    [setAlertConfig]
  )

  const handleDisableMFA = React.useCallback(async () => {
    const confirm = await showDeleteConfirmation()
    if (confirm) {
      const response = await disableMFA()
      if (response.success) {
        toast.success('MFA disabled successfully')
      }
    }
  }, [disableMFA, showDeleteConfirmation])

  return (
    <>
      <Flex>
        <Button
          $primary
          $softDanger={mfaSetupComplete}
          disabled={isLoading}
          onClick={async () => {
            if (mfaSetupComplete) {
              await handleDisableMFA()
            } else {
              setIsOpen(true)
            }
          }}
        >
          {`${prefix} Multi-Factor Authentication (MFA)`}
        </Button>
      </Flex>
      <Modal
        closeButton
        onHide={() => setIsOpen(false)}
        show={isOpen}
        title="Setup MFA"
        withStopPropagation={false}
      >
        <Flex
          $flexDirection="col"
          $verticalSpacing="medium"
          align="center"
          justify="center"
        >
          {isLoading && <Spinner color="onLight" size={56} />}
          {!otpUri && (
            <>
              <Text>Enter your password to enable MFA</Text>
              <Formik
                initialValues={{ password: '' }}
                onSubmit={async (values) => {
                  await handleSetupMFA(values.password)
                }}
                validationSchema={Yup.object().shape({
                  password: Yup.string().required('Password is required'),
                })}
              >
                {({ handleSubmit }) => (
                  <form onSubmit={handleSubmit}>
                    <Space>
                      <FormikPasswordField
                        name="password"
                        // @ts-ignore
                        placeholder="Password"
                      />
                      <Button
                        $primary
                        disabled={isSetupMFALoading}
                        type="submit"
                      >
                        Enable MFA
                      </Button>
                    </Space>
                  </form>
                )}
              </Formik>
            </>
          )}
          {otpUri && (
            <>
              <Flex
                $flexDirection="col"
                $verticalSpacing="medium"
                align="center"
                justify="center"
              >
                <Text>Scan the QR code with your authenticator app</Text>
                <QRCodeSVG value={otpUri} />
                <Flex $horizontalSpacing="medium">
                  <Text className="text-sm text-gray-500">
                    Or copy the code and paste it into your authenticator app
                  </Text>
                  <CopyButton text={otpUri} />
                </Flex>
              </Flex>
              <Space />
              <Formik
                initialValues={{ code: '' }}
                onSubmit={(values) => handleVerifyMFA(values.code)}
                validationSchema={Yup.object().shape({
                  code: Yup.string().required('Code is required'),
                })}
              >
                {({ handleSubmit }) => (
                  <form onSubmit={handleSubmit}>
                    <Space>
                      <FormikTextField
                        name="code"
                        // @ts-ignore
                        placeholder="Code"
                      />
                      <Button
                        $primary
                        disabled={isVerifyMFALoading}
                        type="submit"
                      >
                        Verify MFA
                      </Button>
                    </Space>
                  </form>
                )}
              </Formik>
            </>
          )}
        </Flex>
      </Modal>
    </>
  )
}
